import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { Dispatch } from "redux";
import {
	Box,
	Button,
	styled,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	Typography,
} from "@mui/material";
import {
	AlertTriangle,
	BookOpenCheck,
	CalendarCheck2,
	CalendarCogIcon,
	CheckSquare,
	ChevronRight,
	ClipboardCheck,
	Cross,
	LogOut,
	Menu,
	TriangleAlert,
	Users,
	X,
} from "lucide-react";

// U T I L S
import {
	AppState,
	deleteLoggedUser,
	setIsAuthenticated,
	setHeaderState,
	setIsPatientsEnabled,
	setPageState,
} from "../../store/reducer";
import { getHeaders, URI, URI_API } from "../../utils/config";
import { Position } from "../../views/Login/LoginUtils";
import { PageState } from "../../views/Home/HomeUtils";
import { LoggedUser } from "../../store/interfaces";
import { HeaderState } from "../Header/HeaderUtils";
import ResourceButton from "./ResourceButton";

// A S S E T S
import logo from "../../assets/images/nurasign_color.png";
import "./Sidebar.scss";
import { useEffect, useState } from "react";

export const StyleToolTip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip
		{...props}
		classes={{ popper: className }}
	/>
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: "white",
	},

	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "white",
		width: "12rem",
		borderRadius: "12px",
		color: "rgba(0, 0, 0, 0.87)",
		boxShadow: "3px 1px 30px -2px rgba(0,0,0,0.24)",
		fontSize: 11,
		padding: "7px 0",
	},
}));

export default function Sidebar() {
	const [survey, setSurvey] = useState<any>(null);
	const [isSurveyAnswered, setIsSurveyAnswered] = useState(true);
	const [surveyTooltipOpen, setSurveyTooltipOpen] = useState(true);
	const { instance } = useMsal();

	const isPatientsEnabled = useSelector((state: AppState) => state.isPatientsEnabled);
	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);
	const pageState: PageState = useSelector((app: AppState) => app.pageState);

	const dispatch: Dispatch<any> = useDispatch();

	const optionIsSelect = (option: PageState): string => {
		return option === pageState ? "sidebar__option--selected" : "";
	};

	const logOut = async (): Promise<void> => {
		dispatch(setIsAuthenticated(false));
		await instance.logoutRedirect();
		await fetch(`${URI}/logout`, getHeaders()).then((_) => {
			dispatch(deleteLoggedUser());
			dispatch(setIsPatientsEnabled(false));
			dispatch(setHeaderState(HeaderState.Normal));
			localStorage.removeItem("isAuthenticatedNurasign");
		});
	};

	useEffect(() => {
		fetch(`${URI_API}/get_survey_info`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				if (body?.is_survey_answered === null) {
					setIsSurveyAnswered(false);
				}
				setSurvey(body);
			});
	}, []);

	const redirectToManual = () => {
		window.open("/files/manual-usuario-nurasign.pdf", "_blank");
	};

	const redirectToForms = () => {
		window.open("https://forms.cloud.microsoft/r/sJthLsw53F", "_blank");
	};

	const redirectToSurvey = () => {
		if (!isSurveyAnswered) {
			fetch(
				`${URI_API}/update_answered_survey`,
				getHeaders("POST", {
					survey_id: survey?.survey_id,
				}),
			);
			setIsSurveyAnswered(true);
		}
		window.open(survey?.survey_link, "_blank");
	};

	const _onChangePageState = (newPage: PageState) => dispatch(setPageState(newPage));

	const resourcesButtons = [
		<ResourceButton
			icon={ClipboardCheck}
			label="Encuesta"
			onClick={redirectToSurvey}
		/>,
		<ResourceButton
			icon={BookOpenCheck}
			label={
				<>
					Manual de <br /> usuario
				</>
			}
			onClick={redirectToManual}
		/>,
		<ResourceButton
			icon={AlertTriangle}
			label={
				<>
					Reportar <br />
					incidencia
				</>
			}
			onClick={redirectToForms}
		/>,
	];

	return (
		<Box className="sidebar">
			<Box className="sidebar__image">
				<img
					src={logo}
					alt=""
				/>
			</Box>
			<Box className="sidebar__body">
				<Button
					id="onboarding-1"
					onClick={() => _onChangePageState(PageState.AssistanceAdjust)}
					className={`sidebar__option ${optionIsSelect(0)}`}
				>
					<CalendarCogIcon />
					<Typography>
						Ajustes de <br /> asistencia
					</Typography>
				</Button>
				{!isPatientsEnabled && loggedUser.position !== Position.SUPER ? (
					<Tooltip
						title={
							<Box className="sidebar__option__patients">
								<TriangleAlert
									size={40}
									color="#B3261E"
								/>
								<Typography className="sidebar__option__patients-tooltip-text">
									Por favor, confirma tus Ajustes de Asistencia.
								</Typography>
							</Box>
						}
						placement="right"
						arrow
						classes={{
							tooltip: "sidebar__option__patients-tooltip",
							arrow: "sidebar__option__patients-tooltip-arrow",
						}}
					>
						<Box>
							<Button
								id="onboarding-4"
								onClick={
									isPatientsEnabled
										? () => _onChangePageState(PageState.Patients)
										: undefined
								}
								disabled={!isPatientsEnabled}
								className={`sidebar__option ${optionIsSelect(1)} ${
									!isPatientsEnabled && "sidebar__option--disabled"
								}`}
							>
								<Users />
								<Typography>Pacientes</Typography>
							</Button>
						</Box>
					</Tooltip>
				) : (
					<Button
						id="onboarding-4"
						onClick={
							isPatientsEnabled || loggedUser.position === Position.SUPER
								? () => _onChangePageState(PageState.Patients)
								: undefined
						}
						disabled={!isPatientsEnabled && loggedUser.position !== Position.SUPER}
						className={`sidebar__option ${optionIsSelect(1)} ${
							!isPatientsEnabled &&
							loggedUser.position !== Position.SUPER &&
							"sidebar__option--disabled"
						}`}
					>
						<Users />
						<Typography>Pacientes</Typography>
					</Button>
				)}
				<Button
					id="onboarding-9"
					onClick={() => _onChangePageState(PageState.PlanificationAssistance)}
					className={`sidebar__option ${optionIsSelect(2)}`}
				>
					<CalendarCheck2 />
					<Typography>
						Planificación
						<br /> de asistencia
					</Typography>
				</Button>
				<Button
					id="onboarding-10"
					onClick={() => _onChangePageState(PageState.NursingFunctions)}
					className={`sidebar__option ${optionIsSelect(3)}`}
				>
					<Cross />
					<Typography>
						Funciones de
						<br /> Enfermería
					</Typography>
				</Button>
				<Button
					id="onboarding-12"
					onClick={() => {
						_onChangePageState(PageState.Dashboard);
						dispatch(setHeaderState(HeaderState.Extended));
					}}
					className={`sidebar__option ${optionIsSelect(4)}`}
				>
					<Menu />
					<Typography>
						Asignación <br /> Diaria
					</Typography>
				</Button>
			</Box>
			<Box>
				<StyleToolTip
					placement="right-start"
					title={<Box className="sidebar__tooltip">{resourcesButtons}</Box>}
				>
					<Tooltip
						title={
							<Box className="sidebar__option__survey-tooltip">
								<CheckSquare />
								<Typography>
									¡Nueva encuesta disponible!
									<br />
									Te invitamos a responderla
									<br />
									para seguir mejorando tu
									<br />
									experiencia.
								</Typography>
								<Button
									className="sidebar__option__survey-tooltip--button"
									onClick={() => setSurveyTooltipOpen(false)}
								>
									<X />
								</Button>
							</Box>
						}
						open={!isSurveyAnswered && surveyTooltipOpen}
						placement="right"
						arrow
						classes={{ tooltip: "sidebar__option__survey-tooltip--tooltip" }}
					>
						<Button
							disableRipple
							sx={{ cursor: "default" }}
							onMouseEnter={() => setSurveyTooltipOpen(false)}
							className={`sidebar__option`}
						>
							<ChevronRight />
							<Typography>Soporte y Recursos</Typography>
						</Button>
					</Tooltip>
				</StyleToolTip>
				<Button
					onClick={logOut}
					className="sidebar__option sidebar__option--logout"
				>
					<LogOut />
					<Typography>Cerrar sesión</Typography>
				</Button>
			</Box>
		</Box>
	);
}
